import React, { useMemo } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useFontScale } from '@/hooks/shared/useFontScale';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { GlassCard } from '../GlassCard';

/**
 * Width of a compact card in the horizontally scrolling `row` layout. Fixed so
 * every card in the row is the same size and the next one always peeks past the
 * screen edge — that peek is the only affordance telling the resident the row
 * scrolls. Grid callers override it with a flex basis instead (see
 * `IconStatCardList`), so no screen-width math is needed anywhere.
 */
export const ICON_STAT_CARD_WIDTH = 158;

/** Icon tile and the glyph inside it. Off the shared scale by design (Figma). */
const ICON_TILE_SIZE = 32;
const ICON_GLYPH_SIZE = 16;
const ICON_TILE_RADIUS = 10;

type IconStatIcon = React.ComponentType<{
  width?: number;
  height?: number;
  color?: string;
}>;

interface IconStatCardProps {
  label: string;
  caption: string;
  iconBg: string;
  Icon: IconStatIcon;
  iconColor: string;
  onPress: () => void;
  /**
   * Override the card's own sizing. The default is a fixed `ICON_STAT_CARD_WIDTH`;
   * a grid parent passes a flex basis, which wins over `width` on the main axis.
   */
  itemStyle?: StyleProp<ViewStyle>;
  /**
   * When true, a leading numeric count in the caption (e.g. "3 open") is
   * emphasized: a corner badge on the icon plus the number rendered bold in the
   * card's accent color. No badge shows when the count is 0. Used by Quick
   * Actions to surface new/unread/open counts.
   */
  showCountBadge?: boolean;
}

/**
 * Compact icon-stat card used by the home screen's Quick Actions and My
 * Community rows. A single low row — 32px icon tile, then label over caption —
 * so four or five of them fit on screen without the home page scrolling.
 */
export const IconStatCard: React.FC<IconStatCardProps> = React.memo(({
  label,
  caption,
  iconBg,
  Icon,
  iconColor,
  onPress,
  itemStyle,
  showCountBadge = false,
}) => {
  const type = useTypography();
  const space = useSpacing();
  // The home page is a no-scroll page whose fit ladder measures this row, so
  // the label may only take a second line at large text; at 1x it ellipsizes
  // exactly as before ("Linked Accounts" would otherwise wrap at 1x and grow
  // the whole My Community row by a line).
  const { isLargeText } = useFontScale();
  const styles = useThemedStyles(({ colors }) =>
    StyleSheet.create({
      itemWrapper: {
        width: ICON_STAT_CARD_WIDTH,
      },
      card: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.md,
      },
      iconWrap: {
        position: 'relative',
        // Never let a long label squeeze the tile out of square.
        flexShrink: 0,
      },
      icon: {
        width: ICON_TILE_SIZE,
        height: ICON_TILE_SIZE,
        borderRadius: ICON_TILE_RADIUS,
        alignItems: 'center',
        justifyContent: 'center',
      },
      badge: {
        position: 'absolute',
        top: -6,
        right: -6,
        minWidth: 20,
        minHeight: 20,
        borderRadius: 10,
        paddingHorizontal: 5,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.accent.red,
      },
      badgeText: {
        color: PRIMITIVE_COLORS.white,
        fontSize: 11,
        fontWeight: '700',
        lineHeight: 14,
      },
      textColumn: {
        flex: 1,
        // Without this a long word measures wider than the column and pushes
        // the icon tile off the card instead of ellipsizing.
        minWidth: 0,
      },
      label: {
        ...type.cardTitle,
        color: colors.text.tertiary,
      },
      caption: {
        ...type.caption,
        lineHeight: 16,
        color: colors.text.dim,
      },
      captionCount: {
        fontWeight: '700',
      },
    }),
  );

  // Pull a leading integer out of the caption ("3 open" → 3) so the badge and
  // the bold caption number always agree — both come from the same string.
  const countMatch = showCountBadge ? caption.match(/^(\d+)\b/) : null;
  const count = countMatch ? parseInt(countMatch[1], 10) : 0;
  const hasCount = count > 0;

  return (
    // The card is the pressable itself (no wrapper), so the desktop hover —
    // border, shadow, 1px lift — and the 0.7 press dip land on the card.
    <GlassCard
      interactive
      onPress={onPress}
      borderRadius={REDESIGN_SPACING.quickActionRadius}
      padding={space.md}
      style={[styles.itemWrapper, itemStyle, styles.card]}
      accessibilityRole="button"
      accessibilityLabel={`${label}, ${caption}`}
    >
      <View style={styles.iconWrap}>
        <View style={[styles.icon, { backgroundColor: iconBg }]}>
          <Icon width={ICON_GLYPH_SIZE} height={ICON_GLYPH_SIZE} color={iconColor} />
        </View>
        {hasCount && (
          <View style={styles.badge}>
            <Text style={styles.badgeText} numberOfLines={1} scale="micro">
              {count > 99 ? '99+' : count}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.textColumn}>
        <Text style={styles.label} numberOfLines={isLargeText ? 2 : 1}>
          {label}
        </Text>
        {/* One line, always: a wrapped caption makes this card taller than
            its row siblings, and the row reads as misaligned. */}
        {hasCount ? (
          <Text style={styles.caption} numberOfLines={1}>
            <Text style={[styles.captionCount, { color: iconColor }]}>{countMatch![1]}</Text>
            {caption.slice(countMatch![1].length)}
          </Text>
        ) : (
          <Text style={styles.caption} numberOfLines={1}>
            {caption}
          </Text>
        )}
      </View>
    </GlassCard>
  );
});

IconStatCard.displayName = 'IconStatCard';

export type IconStatCardLayout = 'row' | 'grid';

export interface IconStatCardItem {
  key: string;
  label: string;
  caption: string;
  iconBg: string;
  Icon: IconStatIcon;
  iconColor: string;
  onPress: () => void;
}

interface IconStatCardListProps {
  items: IconStatCardItem[];
  /**
   * `row` (default, mobile) is an edge-bleeding horizontal scroller; `grid`
   * (desktop) wraps two-up inside the column it is given.
   */
  layout?: IconStatCardLayout;
  /**
   * Page padding the `row` layout bleeds past so cards run to the screen edge
   * and back. Must match the screen's own horizontal padding.
   */
  horizontalPadding?: number;
  /** Extra per-card sizing, applied after the layout's own. */
  itemStyle?: StyleProp<ViewStyle>;
  showCountBadge?: boolean;
}

/**
 * Lays a set of compact cards out either as a scrolling row or a wrapping grid.
 * Shared by Quick Actions and My Community so the two sections cannot drift.
 */
export const IconStatCardList: React.FC<IconStatCardListProps> = React.memo(({
  items,
  layout = 'row',
  horizontalPadding = REDESIGN_SPACING.horizontalPadding,
  itemStyle,
  showCountBadge = false,
}) => {
  const styles = useThemedStyles(() =>
    StyleSheet.create({
      grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: REDESIGN_SPACING.gridGap,
      },
      // flexBasis beats `width` on the main axis, so this cancels the card's
      // default fixed width without needing to unset it.
      gridItem: {
        flexBasis: '48%',
        flexGrow: 1,
      },
      rowContent: {
        gap: REDESIGN_SPACING.gridGap,
      },
    }),
  );

  // Edge bleed: pull the scroller out past the page padding, then put the same
  // padding back inside so the first and last card still line up with the text
  // above them while the row itself runs to both screen edges.
  const bleedStyle = useMemo(
    () => ({ marginHorizontal: -horizontalPadding }),
    [horizontalPadding],
  );
  const rowPaddingStyle = useMemo(
    () => ({ paddingHorizontal: horizontalPadding }),
    [horizontalPadding],
  );

  const cards = items.map((item) => (
    <IconStatCard
      key={item.key}
      label={item.label}
      caption={item.caption}
      iconBg={item.iconBg}
      Icon={item.Icon}
      iconColor={item.iconColor}
      onPress={item.onPress}
      itemStyle={layout === 'grid' ? [styles.gridItem, itemStyle] : itemStyle}
      showCountBadge={showCountBadge}
    />
  ));

  if (layout === 'grid') {
    return <View style={styles.grid}>{cards}</View>;
  }

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={bleedStyle}
      contentContainerStyle={[styles.rowContent, rowPaddingStyle]}
    >
      {cards}
    </ScrollView>
  );
});

IconStatCardList.displayName = 'IconStatCardList';
